import { AccountType } from 'config'
import type { ILoanGlobalSettings } from 'pages/Admin/AdminTools/Configuration/GlobalSettings'
import { AllowCashOutType } from 'pages/LoanSubmission/types'
import type { IMinMaxLimit, INonDscrLTVs, IRange, IVisibleOptions } from 'pages/Resources'
import { LoanType } from 'reducers/loanDetail.reducer'

export interface ILLPA {
  name: string
  isEnabled: boolean
  value: number
  label?: string
  from?: number
  to?: number
}

export interface ILockTermPrice {
  LockDay: string
  BasePrice: number
  LockTermLLPA: number
  Price: string
  type?: string
  IsPPPMaxPrice?: string
}

export interface IPrice_Program_Price {
  Rate: number
  BaseRate: number
  Dscr?: number
  RateSpread?: number
  ExitFee?: number
  LockTermPrices: ILockTermPrice[]
  CalcHistory?: string[]
  LLPA?: Record<string, ILLPA>
  LLRA?: Record<string, ILLPA>
  Limit?: IPriceLimit
  SubProgramID?: number
}

export interface IPrice_Program {
  ProgramID: number
  ProgramManualID?: number
  ProgramName: string
  TierLevel: number
  Margin: number
  Prices: IPrice_Program_Price[]
  LLPA: Record<string, ILLPA>
  CalcHistory: string[]
  LTV: IRange
  LockInfo?: {
    Margin: number
    LLPA: Record<string, ILLPA>
    LLRA: Record<string, ILLPA>
    CalcHistory: string[]
    LTV: IRange
  }
}

export interface IPrice {
  ProductID: number
  ProductName: string
  InterestOnly?: boolean
  Programs: IPrice_Program[]
}

export interface IinEligibleObject {
  name: string
  value: string
  description: string
  ltvMaxCalcHistory?: string[]
}

export interface IinEligiblePrograms {
  [key: string]: IinEligibleObject[]
}

export interface IInElibileProduct {
  [key: string]: IinEligiblePrograms
}

export interface IProduct {
  Id: number
  Description: string
  IsInterestOnly?: boolean
  Power?: boolean
  Term?: number
  Type?: string
}

export interface IProgram {
  ID: number
  Name: string
  PriceOption: string
  ManualID?: number
  LtvLimit?: IPriceLimit
  TotalLTVLimits?: INonDscrLTVs
  LienType?: LienType
  UserTypes?: AccountType[]
  ChannelTypes?: LoanType[]
  VisibleOptions?: IVisibleOptions
  MaxLoanAmount?: number
}

export interface ILoanProcess {
  type: string
  interestOnly?: boolean
  program: string
  subProgramID?: number
  rate: number
  price: number
  sheetDate: string
  rateSpread: number
  exitFee: number
  lockDays: number
  tierLevel?: number
  lockedDate?: string
  expireDate?: string
  rateLocked?: boolean
  DSCR?: number
  pricingRequested: boolean
  restructureApplicationRelease?: boolean
  extensions?: number[]
  priceOption?: string
  priceAdjustments?: IPriceAdjustment[]
  [key: string]: any
}

export interface IPriceLimit {
  Rate?: number
  aiv_ltv?: number
  arv_ltv?: number
  ltc?: number
  ltp?: number
  cltv?: number
  max_aiv_ltv?: number
  max_arv_ltv?: number
  max_ltc?: number
  max_ltp?: number
  max_cltv?: number
  b_piece_aiv_ltv?: number
  b_piece_arv_ltv?: number
  b_piece_ltc?: number
  b_piece_ltp?: number
}

export interface IMultipleProperty {
  id: number
  subjectPropertyAddress: string
  subjectPropertyAddressTBD: boolean
  transactionType: string
  lienType: LienType
  proposedOccupancy: string
  propertyHasBeenListed: boolean
  propertyPurchasePrice: number
  asIsValue: number
  afterRepairValue: number
  rehabBudget: number
  constructionReserve: number
  costsSpentToDate: number
  secondLien: number
  secondLienPayment: number
  lienPayoff: number
  propertyType: string
  condoType: string
  numberOfUnits: number
  vacant: number
  vacantIsPercentage: boolean
  proposedMonthlyRent: number
  proposedMonthlyTaxes: number
  proposedMonthlyInsurance: number
  proposedMonthlyHoaDues: number
  floodInsurance: number
  schoolTax: number
  otherTax: number
}

export enum LienType {
  FIRST_LIEN,
  SECOND_LIEN,
  THIRD_LIEN,
}

export const LienTypeOption: Record<LienType, string> = {
  [LienType.FIRST_LIEN]: '1st Lien',
  [LienType.SECOND_LIEN]: '2nd Lien',
  [LienType.THIRD_LIEN]: '3rd Lien',
}

export interface ILoan {
  id: number
  no: number
  byteproFileName: number
  productType: string
  transactionType: string
  docType: string
  lienType: LienType
  interestType: string
  proposedOccupancy: string
  loanTerm: number
  borrowerType: string
  proposedLoanAmount: number
  isBPiece: boolean
  aPieceAmount: number
  propertyPurchasePrice: number
  asIsValue: number
  afterRepairValue: number
  rehabBudget: number
  interestReserve: number
  chargeInterestOnReserve: boolean
  constructionReserve: number
  costsSpentToDate: number
  lienPayoff: number
  closingCostEstimate: number
  escrowRepairHoldback: number
  experience: string
  howManyExperiences: number
  proposedMonthlyTaxes: number
  proposedMonthlyInsurance: number
  proposedMonthlyRent: number
  proposedMonthlyHoaDues: number
  floodInsurance: number
  schoolTax: number
  otherTax: number
  propertyType: string
  condoType: string
  prepaymentPenalty: string
  residency: string
  amortizationType: string
  escrowType: string
  countryOfCreditScore: string
  isShortTermRental: boolean
  firstTimeHomeBuyer: boolean
  firstTimeHomeInvestor: boolean
  propertyUnderConstruction: boolean
  propertyHasBeenListed: boolean
  ruralProperty: boolean
  manufacturedHousing: boolean
  isMultipleProperties: boolean
  includeInterestReserve: boolean
  decliningMarketProperty: boolean
  howManyProperties: number
  bankruptcy: string
  foreclosure: string
  deedInLieu: string
  shortSale: string
  mortgageLates: string
  secondLien: number
  secondLienPayment: number
  existingLienholder: string
  monthsReserve: number
  estimatedDscr: number
  numberOfUnits: number
  vacant: number
  vacantIsPercentage: boolean
  borrowerFirstName: string
  borrowerMiddleName: string
  borrowerLastName: string
  entityTitle: string
  estimatedCreditScore: number
  isNoScore: boolean
  propertyState: string
  subjectPropertyAddress: string
  subjectPropertyAddressTBD: boolean
  geoLocation: IGeoLocation
  multipleProperties: IMultipleProperty[]
  additionalPropertyAddress: any[]
  propertyCounty: string
  apartmentUnit: number
  parcelNo: string
  yearAcquired: number
  amountExistingLiens: number
  nyBlock: string
  nyLot: string
  includeCoborrower: boolean
  type: LoanType
  deleted: boolean
  isSample: boolean
  /** Held-for-sale property, not a deal: kept out of the pipeline, reports and dashboard. */
  isInventory: boolean
  createdAt: Date
  updatedAt: Date
  ltvAdjustment: number
  cltvAdjustment: number
  aivLtvAdjustment: number
  arvLtvAdjustment: number
  ltcAdjustment: number
  ltpAdjustment: number
  allowCashOut: AllowCashOutType
  allowCashOutPercentage: number
  underwritingFee: number

  changedLoanDetailJson?: Record<string, ILoanDetailChangedJson>
  changedPropertyDetailJson?: IPropertyChangedJson[]
  selectedLoanChanges?: string[]
  selectedPropertyChanges?: {
    index: number
    key: string
  }[]
}

export interface ICrossCollateralBlanket {
  numberOfPropertiesLimit: IMinMaxLimit
  propertyLevelAllocatedBalanceLimit: IMinMaxLimit
  eligibleOccupancies: string[]
  eligiblePropertyTypes: string[]
  minAsIsValue: number
  minPurchasePrice: number
  minDscr: {
    propertyTypes: string[]
    amortizingPaymentMinDscr: number
    interestOnlyMinDscr: number
  }[]
}

export interface ILoanDetail {
  loanNumber: string
  byteproFileName: number
  entityTitle: string
  borrowerName: string
  propertyAddress: string
  loanProcessDate: string
  loanStatus: string
  servicingStatus: any
  servicingPipeline: boolean
  includeCoborrower: boolean
  loanType: LoanType
  parties: Record<string, any>
  closeParties: Record<string, any>
  rateData: Record<string, any>
  priceAdjustments: IPriceAdjustment[]
  restructureApplicationRelease: boolean
  closingDate: string
  emailContacts: any[]
  rush: boolean
  submitToLoanSetup: number
  submitConditions: number
  submitToUnderwriter: number
  termSheetSignedDate?: string
  termSheetSignedPdfKey?: string
  termSheetWetSigned?: boolean
  termSheetOverride?: boolean
  pushedToYieldeasyAt: any
  // crossCollateralBlanketLimit: ICrossCollateralBlanket
  // allocatedLoanAmount: number[]
  loanGlobalSettings: ILoanGlobalSettings
  pricingEngine: boolean
  closingCostEstimate: number
  templateNumber?: number
  loanIntakeTaskCleared?: boolean
  borrowingEntityName?: string
  servicerLoanNumber?: number
  thirdPartyData?: any
  mers?: any
  thirdPartyId?: any
  loanSubStatus?: any
  isCreatedByBorrower?: boolean
  createdAt?: Date
  reviewForm?: {
    appraiserAsIsProperty: number
    appraiserAfterRepairProperty: number
  }
}

export interface ILockDay {
  description: string
  Power?: boolean
}

export interface ILoanDetailChangedJson {
  key: string
  label: string
  origin: any
  changed: any
}

export interface IPropertyChangedJson {
  id: number
  index: number
  key: string
  label: string
  origin: any
  changed: any
}

export interface IPendingLoanChangeRequestData {
  loanDetailChanges: Record<string, ILoanDetailChangedJson>
  propertyChanges: IPropertyChangedJson[]
  removedPropertyIDs: number[]
  pricingChanges: ILoanProcess
  taskID: number
  type: number
  requestId?: string
}

export enum RateLockRequestType {
  Lock = 4,
  Relock = 5,
  LockExtension = 6,
}

export enum RateLockRequestAction {
  Request = 'request',
  Approve = 'approve',
  Reject = 'reject',
  Cancel = 'cancel',
}

export enum LockExtensionMode {
  Direct = 'direct',
  Request = 'request',
}

export interface IRateLockRequest {
  requestId: string
  taskID?: number
  taskNo?: number
  type: RateLockRequestType
  status: number
  payload: Record<string, any>
  by: string
  time: number
  completedBy?: string
  completedDate?: number
  comment?: string
}

export interface IPriceAdjustment {
  id: number
  note: string
  price: string
  rate: string
  type: string
  min?: string
  max?: string
}

export interface ILoanRateSheet {
  id?: number
  loanNumber?: number
  ratesheetID: number | undefined
  data: {
    selectedProgram: IPrice_Program_Price[]
    relockTimes: number
    prices: IPrice[]
    priceLoanTime: string
    programs: IProgram[]
    products: IProduct[]
    priceAdjustments: IPriceAdjustment[]
    lockDays: ILockDay[]
    brokerMargin: { rate: number; price: number }
    limit: IPriceLimit | undefined
    selectedProductId: number | undefined
    selectedProgramId: number | undefined
    priceIndex: { index1: number; index: number }
  }
}

export interface ISaveToPipelineParams {
  ratesheetJson?: ILoanRateSheet
  loanProcessDataJson?: ILoanProcess
}

export interface IConcessionData {
  note: any
  value: any
  date: string
}

export interface ITermSheetPlaceholders {
  LoanAmount: string
  '120%LoanAmount': string
  LTV: string
  BorrowerName: string
  BorrowerCompanyName: string
  BrokerStreet: string
  BrokerCity: string
  BrokerState: string
  BrokerZipCode: string
  LenderCompany: string
  SubjectPropertyAddress: string
  InterestRate: string
  LoanTerm: string
  DiscountFeePercent: string
  DiscountFeeAmount: string
  CashBorrowerAtClosing: string
  ExitFee: string
  ConstructionBudget: string
  ConstructionAmountFinanced: string
  BrokerFeeAmount: string
  BrokerFeePercent: string
  BrokerCompanyName: string
  UnderwritingFee: string
  BorrowerType: string
  FiveLaterDate: string
  TodayDate: string
  LoanOfficerOrBrokerName: string
  EstimatedCreditScore: string
  CommitmentFee: string
  BreakUpFee: string
  DueDilligence: string
  MonthlyPaymentNonEscrow: string
  MonthlyPaymentWithEscrow: string
  PrepaymentPenalty: string
  PurchasePrice: string
  DSCRValue: string
  LTC: string
  LTARV: string
  MonthlyPaymentInitialNonDutch: string
  InitialLoanAmount: string
  BorrowerEntityTitle: string
  BorrowerPresentAddress: string
  AmortizationType: string
  AsIsValue: string
  ProcessingFeeAmount: string
  SixMonthsInterestPaymentReserve: string
  TenPercentConstructionBudget: string
  ProcessingFeePlusOriginatorFee: string
}

export interface IGeoLocation {
  lat: number
  lng: number
}

export interface ILoanDetailsObject {
  [key: string]: {
    limit: IRange
    value: any
    isPassed: boolean
    ineligibleText: string
    calcHistory?: string[]
  }
}

export interface IProgramLoanDetailObject {
  [key: string]: ILoanDetailsObject
}

export interface IProgramLoanDetails {
  [key: string]: IProgramLoanDetailObject
}

export interface IExtensionCost {
  extensions: {
    extension: number
    priceAdjustment: number
  }[]
  maxExtensionDays: number
  maxExtensionCount: number
}

export interface IBorrowerTypeLimitType {
  value: string
  borrowerTypes: string[]
}

export interface IBorrowerTypeLimit {
  firstTimeHomeBuyerLimit: IBorrowerTypeLimitType[]
  firstTimeHomeInvestorLimit: IBorrowerTypeLimitType[]
}

export interface ILoanOption {
  loanRateSheet: ILoanRateSheet
  loanProcessData: ILoanProcess
  loan: ILoan
  originationFee: number
  processingFee: number
  legalDocFee: number
  mortgageBrokerFee: number
  mortgageBrokerProcessingFee: number
  estimated3rdPartyFee: number
  selected: boolean
  id: number
}

export enum LoanStructureMenu {
  DETAILS = 'loanDetail',
  COMPARATIVE_PRICING = 'comparativePricing',
  REVIEW_AND_LOCK = 'reviewAndLock',
}

export interface ILoanProgramDetails {
  id: number
  no: string
  programCode: string
  productName: string
  programID: number
  mortgageType: string
  loanTerm: string
  amortizationType: string
  interestOnly: boolean
  ballonPayment: boolean
  negativeAmortization: boolean
  otherNonAmortizingFeature: boolean
  ioMonths: number
  jumbo: string
  ARMIndexValue: number
  ARMIntRateFixedFor: number
  ARMIntRateAdjustAt: number
  ARMIndexValuesLocked: boolean
  ARMLifeCap: number
  ARMFloor: number
  ARMNoAdjCap: boolean
  ARMAdjustCapFirst: number
  ARMAdjustCapSubsequent: number
  ARMRounding: number
  ARMMaxRate: number
  ARMCommonRenewal: number
  ARMMargin: number
  ARMHasNegAmFeature: boolean
  ARMIndexType: number
  ARMIndexNameOV: string
  ARMIndexPublished: string
  ARMIndexValueForWeekEnding: string
  ARMLookbackDays: number
  ARMInterestRateChangesDescOV: string
  ARMPaymentChangesDescOV: string
  ARMNegAmDesc: string
  ARMAddFeatures: string
  DemandFeature: boolean
  ARMCarryover: boolean
  ARMConversationOption: boolean
  createdAt: Date
  updatedAt: Date
}

export interface IRatePriceAdjustmentTableData {
  title: string
  value: string
  adjustment: number
}
